import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { pushFaroMeasurement } from "../services/faro";

const MEASUREMENT_TYPE = "dispatch_results_displayed";
const PAINT_WAIT_TIMEOUT_MS = 2000;

const isDocumentHidden = () => document.visibilityState === "hidden";

const toCount = (value) =>
  Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;

const toDuration = (value) => Math.max(0, Math.round(value));

const buildMeasurement = (timing, paintedAt) => {
  const iso = (at) =>
    new Date(timing.originWallMs + (at - timing.originPerf)).toISOString();
  const renderBaseline =
    timing.etaCompletedAt ?? timing.responseReceivedAt ?? timing.completedAt;
  const processEnd = timing.etaStartedAt ?? timing.processCompletedAt;
  const context = { ...timing.context };

  context.dispatch_started_at = iso(timing.startedAt);
  if (timing.responseReceivedAt != null) {
    context.dispatch_ended_at = iso(timing.responseReceivedAt);
  }
  if (timing.etaStartedAt != null) {
    context.here_started_at = iso(timing.etaStartedAt);
  }
  if (timing.etaCompletedAt != null) {
    context.here_ended_at = iso(timing.etaCompletedAt);
  }
  context.rendered_at = iso(paintedAt);

  return {
    values: {
      api_duration_ms: toDuration(
        (timing.responseReceivedAt ?? timing.completedAt) - timing.startedAt
      ),
      process_duration_ms: toDuration(
        timing.responseReceivedAt != null && processEnd != null
          ? processEnd - timing.responseReceivedAt
          : 0
      ),
      eta_api_duration_ms: toDuration(timing.etaDurationMs),
      render_duration_ms: toDuration(paintedAt - renderBaseline),
      total_duration_ms: toDuration(paintedAt - timing.startedAt),
      returned_result_count: timing.returnedResultCount,
      eta_requested_count: timing.etaRequestedCount,
      eta_returned_count: timing.etaReturnedCount,
      eta_failed_count: timing.etaFailedCount,
      rendered_result_count: timing.renderedResultCount,
    },
    context,
  };
};

const createTiming = (timingId, metadata = {}) => {
  const startedAt = performance.now();
  return {
    id: timingId,
    originPerf: startedAt,
    originWallMs: Date.now(),
    startedAt,
    responseReceivedAt: null,
    processCompletedAt: null,
    etaStartedAt: null,
    etaCompletedAt: null,
    etaDurationMs: 0,
    completedAt: null,
    renderScheduled: false,
    emitted: false,
    context: {
      status: "pending",
      eta_status: "pending",
      view: String(metadata.view ?? "unknown"),
      mode: String(metadata.mode ?? "provider"),
      ...(metadata.requestId == null
        ? {}
        : { request_id: String(metadata.requestId) }),
    },
    returnedResultCount: 0,
    etaRequestedCount: 0,
    etaReturnedCount: 0,
    etaFailedCount: 0,
    renderedResultCount: 0,
  };
};

export const useDispatchRenderTiming = () => {
  const sequenceRef = useRef(0);
  const activeTimingRef = useRef(null);
  const firstFrameRef = useRef(null);
  const secondFrameRef = useRef(null);
  const paintTimeoutRef = useRef(null);
  const [paintRequest, setPaintRequest] = useState(null);

  const cancelFrames = useCallback(() => {
    if (firstFrameRef.current !== null) {
      cancelAnimationFrame(firstFrameRef.current);
      firstFrameRef.current = null;
    }

    if (secondFrameRef.current !== null) {
      cancelAnimationFrame(secondFrameRef.current);
      secondFrameRef.current = null;
    }

    if (paintTimeoutRef.current !== null) {
      clearTimeout(paintTimeoutRef.current);
      paintTimeoutRef.current = null;
    }
  }, []);

  const emitImmediately = useCallback((timing, completedAt) => {
    const measurement = buildMeasurement(timing, completedAt);
    pushFaroMeasurement(
      MEASUREMENT_TYPE,
      measurement.values,
      measurement.context
    );
  }, []);

  const startTiming = useCallback((metadata = {}) => {
    cancelFrames();

    const supersededTiming = activeTimingRef.current;
    if (supersededTiming !== null && !supersededTiming.emitted) {
      const completedAt = performance.now();
      supersededTiming.emitted = true;
      supersededTiming.completedAt = completedAt;
      supersededTiming.context = {
        ...supersededTiming.context,
        status: "cancelled",
        failed_stage: "superseded",
      };
      emitImmediately(supersededTiming, completedAt);
    }

    const timingId = ++sequenceRef.current;
    activeTimingRef.current = createTiming(timingId, metadata);

    return timingId;
  }, [cancelFrames, emitImmediately]);

  const markResponseReceived = useCallback((timingId, metadata = {}) => {
    const timing = activeTimingRef.current;

    if (timing?.id !== timingId || timing.emitted) {
      return false;
    }

    timing.responseReceivedAt = performance.now();
    timing.returnedResultCount = toCount(metadata.returnedResultCount);
    timing.context = {
      ...timing.context,
      status: "pending",
      view: String(metadata.view ?? timing.context.view ?? "unknown"),
      ...(metadata.dispatchId == null
        ? {}
        : { dispatch_id: String(metadata.dispatchId) }),
    };

    return true;
  }, []);

  const markProcessReady = useCallback((timingId) => {
    const timing = activeTimingRef.current;

    if (
      timing?.id !== timingId ||
      timing.responseReceivedAt === null ||
      timing.processCompletedAt !== null ||
      timing.emitted
    ) {
      return false;
    }

    timing.processCompletedAt = performance.now();
    return true;
  }, []);

  const markEtaStarted = useCallback((timingId, metadata = {}) => {
    const timing = activeTimingRef.current;

    if (
      timing?.id !== timingId ||
      timing.responseReceivedAt === null ||
      timing.etaStartedAt !== null ||
      timing.emitted
    ) {
      return false;
    }

    timing.etaStartedAt = performance.now();
    if (timing.processCompletedAt === null) {
      timing.processCompletedAt = timing.etaStartedAt;
    }
    timing.etaRequestedCount = toCount(metadata.requestedCount);
    return true;
  }, []);

  const markEtaCompleted = useCallback((timingId, metadata = {}) => {
    const timing = activeTimingRef.current;

    if (
      timing?.id !== timingId ||
      timing.responseReceivedAt === null ||
      timing.etaCompletedAt !== null ||
      timing.emitted
    ) {
      return false;
    }

    timing.etaCompletedAt = performance.now();
    timing.etaDurationMs =
      timing.etaStartedAt === null
        ? 0
        : timing.etaCompletedAt - timing.etaStartedAt;
    timing.etaReturnedCount = toCount(metadata.returnedCount);
    timing.etaFailedCount = toCount(metadata.failedCount);
    const etaStatus = String(metadata.status ?? "success");
    const isDegraded = etaStatus === "error" || etaStatus === "partial";
    timing.context = {
      ...timing.context,
      status: isDegraded ? "partial_success" : "success",
      eta_status: etaStatus,
      ...(isDegraded
        ? { failed_stage: "eta_api" }
        : {}),
    };
    return true;
  }, []);

  const markFailure = useCallback((timingId, metadata = {}) => {
    const timing = activeTimingRef.current;

    if (timing?.id !== timingId || timing.emitted) {
      return false;
    }

    const completedAt = performance.now();
    timing.completedAt = completedAt;
    timing.context = {
      ...timing.context,
      status: String(metadata.status ?? "error"),
      eta_status: String(metadata.etaStatus ?? "skipped"),
      failed_stage: String(metadata.failedStage ?? "dispatch_api"),
      ...(metadata.httpStatus == null
        ? {}
        : { http_status: String(metadata.httpStatus) }),
      ...(metadata.errorType == null
        ? {}
        : { error_type: String(metadata.errorType) }),
    };
    timing.renderedResultCount = 0;
    timing.renderScheduled = true;
    setPaintRequest({ timingId });
    return true;
  }, []);

  const markRenderReady = useCallback((timingId, renderedResultCount) => {
    const timing = activeTimingRef.current;

    if (
      timing?.id !== timingId ||
      timing.responseReceivedAt === null ||
      timing.etaCompletedAt === null ||
      timing.renderScheduled ||
      timing.emitted
    ) {
      return false;
    }

    timing.renderScheduled = true;
    timing.renderedResultCount = toCount(renderedResultCount);
    setPaintRequest({ timingId });

    return true;
  }, []);

  const cancelTiming = useCallback(
    (timingId, metadata = {}) => {
      const timing = activeTimingRef.current;
      if (timing?.id !== timingId || timing.emitted) {
        return false;
      }

      const completedAt = performance.now();
      timing.completedAt = completedAt;
      timing.emitted = true;
      timing.context = {
        ...timing.context,
        status: "cancelled",
        eta_status: String(metadata.etaStatus ?? "skipped"),
        failed_stage: String(metadata.failedStage ?? "cancelled"),
      };
      activeTimingRef.current = null;
      cancelFrames();
      emitImmediately(timing, completedAt);
      return true;
    },
    [cancelFrames, emitImmediately]
  );

  useLayoutEffect(() => {
    if (paintRequest === null) {
      return;
    }

    const { timingId } = paintRequest;

    const emitPaint = () => {
      const timing = activeTimingRef.current;
      if (
        timing?.id !== timingId ||
        (timing.responseReceivedAt === null && timing.completedAt === null) ||
        timing.emitted
      ) {
        return;
      }

      const paintedAt = performance.now();
      timing.emitted = true;
      activeTimingRef.current = null;
      cancelFrames();
      emitImmediately(timing, paintedAt);
    };

    if (isDocumentHidden()) {
      emitPaint();
      return;
    }

    const onVisibilityChange = () => {
      if (isDocumentHidden()) {
        cancelFrames();
        emitPaint();
      }
    };

    firstFrameRef.current = requestAnimationFrame(() => {
      firstFrameRef.current = null;

      if (activeTimingRef.current?.id !== timingId) {
        return;
      }

      secondFrameRef.current = requestAnimationFrame(() => {
        secondFrameRef.current = null;
        emitPaint();
      });
    });

    paintTimeoutRef.current = setTimeout(() => {
      paintTimeoutRef.current = null;
      emitPaint();
    }, PAINT_WAIT_TIMEOUT_MS);

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      cancelFrames();
    };
  }, [cancelFrames, emitImmediately, paintRequest]);

  useEffect(
    () => () => {
      activeTimingRef.current = null;
      cancelFrames();
    },
    [cancelFrames]
  );

  return {
    startTiming,
    markResponseReceived,
    markProcessReady,
    markEtaStarted,
    markEtaCompleted,
    markFailure,
    markRenderReady,
    cancelTiming,
  };
};
